// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Type definitions (matching backend)
export type FeedbackSource = 'zendesk' | 'discord' | 'github' | 'email' | 'twitter' | 'forum';
export type SentimentLabel = 'positive' | 'negative' | 'neutral' | 'mixed';
export type FeedbackCategory =
    | 'bug'
    | 'feature-request'
    | 'ux-issue'
    | 'performance'
    | 'pricing'
    | 'documentation'
    | 'integration'
    | 'support';

export interface Feedback {
    id: string;
    source: FeedbackSource;
    title?: string;
    content: string;
    author?: string;
    author_segment?: string;
    url?: string;
    created_at: number;
    analysis?: FeedbackAnalysis;
}

export interface FeedbackAnalysis {
    sentiment_score: number;
    sentiment_label: SentimentLabel;
    category: FeedbackCategory;
    urgency_score: number;
    value_score: number;
    themes: string;
    entities: string;
    action_items: string;
    ai_summary: string;
}

export interface AnalyticsOverview {
    totalFeedback30d: number;
    sentimentAvg: number;
    topCategory: string;
    urgentCount: number;
    sentimentTrend: Array<{ date: string; score: number }>;
    themeDistribution: Array<{ category: string; count: number; sentiment_avg: number }>;
    urgencyValueScatter: Array<{ id: string; urgency: number; value: number; category: string }>;
}

// API client functions
async function fetchAPI<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`);
    if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
    }
    return response.json();
}

async function postAPI<T>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data ? JSON.stringify(data) : undefined
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
    }
    return response.json();
}

export const api = {
    // Feedback endpoints
    getFeedback: (params: {
        source?: FeedbackSource;
        sentiment?: SentimentLabel;
        category?: FeedbackCategory;
        days?: number;
        urgency_min?: number;
        limit?: number;
        offset?: number;
        search?: string;
    }) => {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
                queryParams.append(key, String(value));
            }
        });
        return fetchAPI<{ items: Feedback[]; total: number; limit: number; offset: number }>(
            `/feedback?${queryParams.toString()}`
        );
    },

    getFeedbackById: (id: string) => {
        return fetchAPI<{ feedback: Feedback; related: Feedback[] }>(`/feedback/${id}`);
    },

    // Analytics endpoints
    getAnalyticsOverview: () => {
        return fetchAPI<AnalyticsOverview>('/analytics/overview');
    },

    getAnalyticsThemes: () => {
        return fetchAPI<any>('/analytics/themes');
    },

    getLatestSummary: () => {
        return fetchAPI<any>('/analytics/summary/latest');
    },

    // Admin endpoints
    generateMockData: () => {
        return postAPI<{ success: boolean; inserted: number; message: string }>('/admin/generate-mock-data');
    },

    analyzeAll: () => {
        return postAPI<{ success: boolean; processed: number; message: string }>('/admin/analyze-all');
    }
};
